import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useContext, useEffect, useCallback, } from 'react';
import { Typography, Box, TextField, Grid, IconButton, } from '@mui/material';
import { Circle, MoreVert } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.bubble.css';
import { useFormik } from 'formik';
import 'katex/dist/katex.min.css';
import katex from 'katex';
// Internal dependencies
import { ChatPromptIconSG, SendChatIconSG, ButtonSG } from '../components';
import { ENDPOINTS, EDITOR_COMMANDS, LOG_LEVELS, EDITOR_COMMAND_TYPES, SERVICES, CHAT_CONTEXT, } from '../constants';
import { axiosClient } from '../axios';
import { UserContext } from '../auth';
import { appStyles } from '../App.styles';
import { FONT_SIZES } from './constants';
window.katex = katex;
/**
 * Toolbar
 * @param props ToolbarSG Props
 * @returns JSX Element
 */
export const ToolbarSG = (props) => {
    // Get user credential
    const user = useContext(UserContext);
    // Get translatiom function
    const { t, i18n } = useTranslation();
    // Extract props
    const { editorRef, sendMessage, outputLanguage } = props;
    // Get editor
    const editor = editorRef && editorRef.current && editorRef.current.getEditor();
    const [aiToolbarOpen, setAIToolbarOpen] = useState(false);
    /**
     * Handle Close AI toolbar
     */
    const handleAIToolbarClose = () => {
        setAIToolbarOpen(false);
    };
    let selectedText = '';
    const [/** Latest selection range */ range, setRange] = useState(undefined);
    if (editor) {
        editor.on('selection-change', (range, oldRange, source) => {
            if (range) {
                // If range exist
                if (range.length === 0) {
                    // If there is no selection
                }
                else {
                    // If there is any selection text
                    // Update range
                    setRange(range);
                    // Close AI toolbar
                    handleAIToolbarClose();
                    // Hide More buttons
                    setMoreOpen(false);
                }
            }
        });
        selectedText = range && editor.getText(range.index, range.length);
    }
    /** Handle More Btn Click */
    const handleMoreBtnClick = () => {
        setMoreOpen(!moreOpen);
    };
    // Focus back to the editor
    /**
     * Set back the selection to the editor
     */
    const setBackSeletion = () => {
        setTimeout(() => editor && editor.setSelection(range.index, range.length), 0);
    };
    const formik = useFormik({
        initialValues: {
            /** Chat input message */ message: '',
        },
        validateOnChange: false,
        onSubmit: (values) => {
            var _a, _b, _c;
            try {
                const /** Message send to AI */ message = `${values.message}: "${selectedText}"`;
                sendMessage(message, undefined, '', [], CHAT_CONTEXT.CHAT);
                // Set back the selection to the editor
                setBackSeletion();
                // Reset message
                formik.setFieldValue('message', '');
            }
            catch (err) {
                // Set back the selection to the editor
                setBackSeletion();
                // Reset message
                formik.setFieldValue('message', '');
                // Save error to the log system
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Editor, Toolbar, onSubmit, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
                // Check if the error is from axios
                if ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) {
                    // If yes, notify user with the server error message
                    toast.error((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
                    return;
                }
                ;
                toast.error('Something went wrong. We are figuring it out.');
            }
            ;
        },
    });
    /**
     * Handle chat textfield change
     * @param e Event
     */
    const handleChatTextFieldChange = (e) => {
        e.preventDefault();
        formik.handleChange(e);
    };
    /**
     * Handle AI toolbar open
     * @param e Event
     */
    const handleAIToolbarOpen = (e) => {
        e.preventDefault();
        setAIToolbarOpen(true);
        // Set back the selection to the editor
        setBackSeletion();
    };
    /**
     * Handle Editor Command Click
     * @param commandType Command type
     */
    const handleEditorCommandClick = (commandType) => {
        var _a, _b, _c;
        try {
            const /** Selected text */ description = selectedText;
            let /** Send message */ message = '';
            let /** Message metadata */ metadata = {
                command: commandType,
                targetText: description,
            };
            switch (commandType) {
                case EDITOR_COMMAND_TYPES.REWRITE_IMPROVE:
                    message = `Rewrite '${description}'`;
                    break;
                case EDITOR_COMMAND_TYPES.CONTINUE_WRITING:
                    message = `Expand from '${description}',then continue writing`;
                    break;
                case EDITOR_COMMAND_TYPES.SUMMARIZE:
                    message = `Summarize '${description}'`;
                    break;
                case EDITOR_COMMAND_TYPES.TRANSLATE:
                    message = `Translate '${description}'. Be sure to maintain the tone and context of the original content.`;
                    break;
                case EDITOR_COMMAND_TYPES.RUN_COMMAND:
                    message = `${description}`;
                    break;
                case EDITOR_COMMAND_TYPES.MAKE_SHORTER:
                    message = `The content to edit: ${description}. Make shorter.`;
                    break;
                case EDITOR_COMMAND_TYPES.MAKE_LONGER:
                    message = `The content to edit: ${description}. Enhance and add depth.`;
                    break;
                case EDITOR_COMMAND_TYPES.SIMPLIFY_LANGUAGE:
                    message = `The content to edit: ${description}. Remove redundant phrases, simplify complex sentences, and cut unnecessary details while retain the overall meaning`;
                    break;
                case EDITOR_COMMAND_TYPES.FIX_GRAMMAR:
                    message = `The content to edit: ${description}.

          Please review for grammatical errors amd punctuation mistakes. Only provide the corrected version of the given text. If the input is already grammatically correct and fluent, reply "No error". Do not explain corrections or comment on the content. Never discuss the meaning or context of the text.`;
                    break;
            }
            sendMessage(message, metadata, '', [], CHAT_CONTEXT.CHAT);
            // set back the selection to the editor
            setBackSeletion();
            // Reset message
            formik.setFieldValue('message', '');
        }
        catch (err) {
            // Set back the selection to the editor
            setBackSeletion();
            // Reset message
            formik.setFieldValue('message', '');
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Editor, Toolbar, handleEditorCommandClick, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) {
                // If yes, notify user with the server error message
                toast.error((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
                return;
            }
            ;
            toast.error('Something went wrong. We are figuring it out.');
        }
        ;
    };
    const [/** Shift key up time */ shiftKeyUpTimeStamp, setShiftKeyUpTimeStamp] = useState(0);
    /**
     * Handle Shift & Enter input key up together will send out message
     * @param event Keyboard Event
     * @returns isNotReleaseTogether
     */
    const avoidShiftEnterKeyUpSendOutMsg = (event) => {
        //check if the shift and enter key are release within the buffer time 700ms
        const timeStampDiffBuffer = 700;
        const isReleaseTogther = event.key === 'Enter' && (event.timeStamp - shiftKeyUpTimeStamp <= timeStampDiffBuffer);
        return !isReleaseTogther;
    };
    /**
     * Handle message input key up
     * @param {KeyboardEvent<HTMLInputElement>} event
     * @returns {void}
     */
    const handleMessageInputKeyUp = (event) => {
        try {
            if (event.key === 'Shift' && event.type === "keyup") {
                setShiftKeyUpTimeStamp(event.timeStamp);
            }
            if (event.key === 'Enter' && !event.shiftKey && avoidShiftEnterKeyUpSendOutMsg(event)) {
                const /** Message send to AI */ message = `${formik.values.message}: "${selectedText}"`;
                sendMessage(message, undefined, '', [], CHAT_CONTEXT.CHAT);
                // Set back the selection to the editor
                setBackSeletion();
                // Reset message, attachFiles
                formik.setFieldValue('message', '');
            }
            ;
        }
        catch (err) {
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Editor, Toolbar, handleMessageInputKeyUp, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.EDITOR,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    };
    useEffect(() => {
        if (!editor || !editor.theme) {
            return;
        }
        const tooltip = editor.theme.tooltip;
        if (!tooltip || !tooltip.root) {
            return;
        }
        const /** Link input */ input = tooltip && tooltip.root.querySelector("input[data-link]");
        if (!input || !input.dataset) {
            return;
        }
        // Set link placeholder
        input.dataset.link = 'https://studioglobal.ai/';
        input.placeholder = 'https://studioglobal.ai/';
    }, [editor]);
    const [/** Open More Buttons*/ moreOpen, setMoreOpen] = useState(false);
    const /** Show Core Button */ coreButtonDisplay = useCallback(() => (aiToolbarOpen ? 'none!important' : 'inline-block!important'), [aiToolbarOpen]);
    const /** Show more button */ moreButtonDisplay = useCallback(() => (aiToolbarOpen || !moreOpen ? 'none!important' : 'inline-block!important'), [aiToolbarOpen, moreOpen]);
    return (_jsxs(Box, Object.assign({ id: "toolbar" }, { children: [aiToolbarOpen ?
                (_jsx(Box, Object.assign({ sx: { width: '100%', padding: '0.5rem 0' } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: { width: '100%' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12, sx: { padding: '0 0 0.5rem' } }, { children: _jsx("form", Object.assign({ onSubmit: formik.handleSubmit }, { children: _jsx(Box, Object.assign({ sx: { padding: '0 0.5rem' } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: {
                                                width: '100%',
                                                borderBottom: '1px solid #BFC9C3',
                                                boxShadow: 'none',
                                                padding: '0rem 0 0.375rem',
                                            } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Typography, Object.assign({ variant: 'text12', sx: {
                                                            padding: '0!important',
                                                            color: '#000000',
                                                            boxSizing: 'border-box',
                                                        } }, { children: t('Ask AI') })) })), _jsx(Grid, Object.assign({ item: true, sx: {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '6px',
                                                        margin: '0 8px 0 0',
                                                    } }, { children: _jsx(Circle, { sx: { color: 'green.main', width: '12px', height: '12px' } }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(TextField, { name: 'message', value: formik.values.message, multiline: true, autoComplete: "off", 
                                                        //autoFocus
                                                        variant: "standard", sx: { width: '100%' }, InputProps: {
                                                            disableUnderline: true,
                                                            style: {
                                                                color: '#000000!important',
                                                                padding: '0!important'
                                                            }
                                                        }, inputProps: {
                                                            style: Object.assign(Object.assign({}, appStyles.text14), { padding: '0!important', maxHeight: '30vh', overflow: 'auto' }),
                                                        }, placeholder: `${t('Ask AI anything and get answers')}`, onChange: handleChatTextFieldChange, onKeyUp: handleMessageInputKeyUp }) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(IconButton
                                                    // Send message button
                                                    , Object.assign({ "aria-label": "Toolbar Send message button", 
                                                        //color="green"
                                                        sx: {
                                                            padding: '0!important',
                                                            textTransform: 'none',
                                                            display: 'inline-flex',
                                                            width: '1.5rem!important',
                                                            height: '1.5rem!important',
                                                            color: 'green.main',
                                                            '.ql-bubble .ql-toolbar &:hover': {
                                                                color: '#006B54!important',
                                                            }
                                                        }, type: "submit", disabled: (formik.values.message.length > 0) ? false : true }, { children: _jsx(SendChatIconSG, { sx: { width: '1.5rem!important', height: '1.5rem!important' } }) })) }))] })) })) })) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsxs(Grid, Object.assign({ container: true, sx: {
                                        border: 'none',
                                        maxWidth: '288px',
                                        width: '100%',
                                        padding: '0.5rem 0 0.25rem',
                                        boxShadow: 'none',
                                    } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Typography, Object.assign({ variant: 'text12', sx: {
                                                    padding: '0.25rem 0.75rem 0.25rem 0.5rem!important',
                                                    color: '#000000',
                                                    boxSizing: 'border-box',
                                                } }, { children: t('Edit') })) })), EDITOR_COMMANDS.map((command, idx) => {
                                            const Icon = command.Icon;
                                            return (_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(ButtonSG, Object.assign({ variant: "contained", sx: {
                                                        color: '#191C1B!important',
                                                        width: '100%!important',
                                                        height: 'auto!important',
                                                        textWrap: 'nowrap!important',
                                                        textTransform: 'none',
                                                        display: 'inline-flex!important',
                                                        justifyContent: 'flex-start!important',
                                                        boxShadow: 'none!important',
                                                        padding: '0.25rem 0.75rem 0.25rem 0.5rem!important',
                                                    }, startIcon: _jsx(Icon, { sx: { color: '#000000', margin: '0 0.5rem 0 0' } }), onClick: (e) => {
                                                        e.preventDefault();
                                                        handleEditorCommandClick(command.type);
                                                        editor.theme.tooltip.edit();
                                                        editor.theme.tooltip.hide();
                                                    } }, { children: t(command.type) })) }), command.type));
                                        })] })) }))] })) }))) : (_jsx(Box, Object.assign({ component: 'span', className: "ql-formats" }, { children: _jsx(Grid, Object.assign({ container: true }, { children: _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(ButtonSG, Object.assign({ variant: 'text', color: "green", sx: Object.assign(Object.assign({}, appStyles.text14), { boxShadow: 'none!important', color: 'green.main!important', '&:hover': {
                                    color: '#006B54!important',
                                }, textTransform: 'none!important', textWrap: 'nowrap!important', padding: '0.25rem 0.75rem 0.25rem 0!important', width: 'auto!important', height: 'auto!important', boxSizing: 'border-box!important', display: 'inline-flex!important' }), onClick: handleAIToolbarOpen, startIcon: _jsx(ChatPromptIconSG, { sx: { width: '18px', color: 'green.main', margin: '8px!important' } }) }, { children: t('Ask AI') })) })) })) }))), _jsx(Box, Object.assign({ component: 'span', className: "ql-formats", sx: {
                    display: coreButtonDisplay(),
                    borderLeft: '1px solid',
                    borderLeftColor: 'greyLight.main',
                } }, { children: _jsxs("select", Object.assign({ className: "ql-header", defaultValue: "0" }, { children: [_jsx("option", Object.assign({ value: "1" }, { children: "Heading 1" })), _jsx("option", Object.assign({ value: "2" }, { children: "Heading 2" })), _jsx("option", Object.assign({ value: "3" }, { children: "Heading 3" })), _jsx("option", Object.assign({ value: "4" }, { children: "Heading 4" })), _jsx("option", Object.assign({ value: "5" }, { children: "Heading 5" })), _jsx("option", Object.assign({ value: "0" }, { children: "Normal" }))] })) })), _jsx(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: coreButtonDisplay() } }, { children: _jsx("select", Object.assign({ id: "font-size", className: "ql-size", defaultValue: "16px" }, { children: FONT_SIZES.map((fontSize) => (_jsx("option", Object.assign({ value: fontSize }, { children: parseInt(fontSize) }), fontSize))) })) })), _jsxs(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: coreButtonDisplay() } }, { children: [_jsx("button", { className: "ql-bold" }), _jsx("button", { className: "ql-italic" }), _jsx("button", { className: "ql-underline" }), _jsx("button", { className: "ql-strike" })] })), _jsxs(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: coreButtonDisplay() } }, { children: [_jsx("select", { className: "ql-color" }), _jsx("select", { className: "ql-background" })] })), _jsxs(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: coreButtonDisplay() } }, { children: [_jsx("button", { className: "ql-link" }), _jsx("button", { className: "ql-image" })] })), _jsx(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: coreButtonDisplay() } }, { children: _jsx("select", { className: "ql-align" }) })), _jsx(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: coreButtonDisplay() } }, { children: _jsx("button", Object.assign({ className: "ql-x", onClick: handleMoreBtnClick }, { children: _jsx(MoreVert, { sx: { width: '18px', height: '18px', color: 'green.main' } }) })) })), _jsx(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: moreButtonDisplay() } }, { children: _jsxs("select", Object.assign({ className: "ql-font", defaultValue: "dmsans" }, { children: [_jsx("option", Object.assign({ value: "dmsans" }, { children: "DM Sans" })), _jsx("option", Object.assign({ value: "sansserif" }, { children: "Sans Serif" })), _jsx("option", Object.assign({ value: "serif" }, { children: "Serif" })), _jsx("option", Object.assign({ value: "monospace" }, { children: "Monospace" }))] })) })), _jsxs(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: moreButtonDisplay() } }, { children: [_jsx("button", { className: "ql-list", value: "ordered" }), _jsx("button", { className: "ql-list", value: "bullet" }), _jsx("button", { className: "ql-indent", value: "-1" }), _jsx("button", { className: "ql-indent", value: "+1" })] })), _jsxs(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: moreButtonDisplay() } }, { children: [_jsx("button", { className: "ql-formula" }), _jsx("button", { className: "ql-script", value: "sub" }), _jsx("button", { className: "ql-script", value: "super" })] })), _jsxs(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: moreButtonDisplay() } }, { children: [_jsx("button", { className: "ql-blockquote" }), _jsx("button", { className: "ql-code-block" })] })), _jsx(Box, Object.assign({ component: 'span', className: "ql-formats", sx: { display: moreButtonDisplay() } }, { children: _jsx("button", { className: "ql-direction", value: "rtl" }) }))] })));
};
